import { useCallback, useEffect } from 'react';
import { pageDataService } from 'common';
import { isITPSupported } from 'common/utilities/FlightSetting';
import { ApiCallResultStatus } from 'store/apiCallResultStatus';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import { getITPFamilyMemberFromCsid } from '../identityMonitoringSelectors';
import { getCompleteSubscriberProfileAsync } from '../identityMonitoringSlice';
import { createDefaultMemberSubscriberProfileState } from '../identityMonitoringState';

import { useIdentityOnboardingStatus } from './useIdentityOnboardingStatus';

/**
 * Helper hook for getting complete subscriber profile
 *
 * @param csidOverride specific csid to get complete subscriber profile for
 */
export const useCompleteIdentityProfile = (csidOverride?: string) => {
  const dispatch = useAppDispatch();
  const csid = useAppSelector((state) => csidOverride || state.identityMonitoring.userContext.csid);
  const member = useAppSelector((state) => getITPFamilyMemberFromCsid(state.identityMonitoring, csid));
  const { componentKey } = useAppSelector((state) => state.webView);

  const { itmOnboarded } = useIdentityOnboardingStatus();

  const shouldLoadCompleteSubscriberProfile = !!csid && isITPSupported() && itmOnboarded;

  const loadCompleteIdentityProfile = useCallback(
    (force = true) => {
      if (shouldLoadCompleteSubscriberProfile) {
        const thunkCreator = force ? getCompleteSubscriberProfileAsync.forceRequest : getCompleteSubscriberProfileAsync;
        return dispatch(thunkCreator(csid));
      }
      return null;
    },
    [dispatch, csid, shouldLoadCompleteSubscriberProfile]
  );

  // fetch complete subscriber profile
  // Note WebView componentKey is added to re-fetch identity profile for a new dialog
  // TODO: We could skip re-fetching identity profile in WebView by:
  // 1. calling it when WebView dialog is closing (requires client-side change)
  // 2. or calling it after adding/removing identities
  useEffect(() => {
    loadCompleteIdentityProfile(false || pageDataService.isWebView());
  }, [loadCompleteIdentityProfile, componentKey]);

  return {
    getCompleteIdentityProfileApiStatus: member?.getCompleteSubscriberProfileApiStatus || ApiCallResultStatus.DEFAULT,
    primaryEmailAddress: member?.primaryEmailAddress,
    subscriberProfile: member?.subscriberProfile || createDefaultMemberSubscriberProfileState(),
    reloadCompleteIdentityProfile: loadCompleteIdentityProfile,
  };
};
